import { useEffect, useState } from 'react';

import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { LinearEngineParams } from '@/types/price-structure';

import { DEFAULT_LINEAR_PARAMS } from '../constants';

interface EngineTuningModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    domainName: string;
    params: LinearEngineParams;
    onApply: (patch: Partial<LinearEngineParams>) => void;
}

type Field = {
    key: 'speed' | 'min_opportunities' | 'deadband' | 'floor_mult' | 'ceil_mult';
    label: string;
    help: string;
    step: number;
    min: number;
};

const FIELDS: Field[] = [
    {
        key: 'speed',
        label: 'Velocidade',
        help: 'Quanto da distância até o alvo ele corrige por dia. 0,333 ≈ um terço: chega sem oscilar.',
        step: 0.01,
        min: 0.01,
    },
    {
        key: 'min_opportunities',
        label: 'Mín. de oportunidades',
        help: 'Abaixo disso no dia, a regra não mexe — pouca amostra dá preenchimento instável.',
        step: 10,
        min: 1,
    },
    {
        key: 'deadband',
        label: 'Zona morta',
        help: 'Distância relativa ao alvo que não gera ajuste. 0,05 = até 5% do alvo.',
        step: 0.01,
        min: 0,
    },
    {
        key: 'floor_mult',
        label: 'Piso (× eCPM)',
        help: 'A regra nunca fica abaixo disso. 1,625 = a cascata nunca abre abaixo de 1,30 × eCPM.',
        step: 0.05,
        min: 0.1,
    },
    {
        key: 'ceil_mult',
        label: 'Teto (× eCPM)',
        help: 'A regra nunca passa disso — segura a escalada quando quase tudo vende.',
        step: 0.1,
        min: 0.1,
    },
];

/** Os dials finos do otimizador do v4. Termômetro e alvo ficam na linha da grade. */
export function EngineTuningModal({
    open, onOpenChange, domainName, params, onApply,
}: EngineTuningModalProps) {
    const [draft, setDraft] = useState(params);

    useEffect(() => {
        if (open) setDraft(params);
    }, [open, params]);

    const invalid = draft.floor_mult > draft.ceil_mult;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Ajustes do otimizador · {domainName}</DialogTitle>
                    <DialogDescription>
                        Todo dia: regra nova = regra de ontem × exp(velocidade × erro), com erro =
                        (preenchimento − alvo) ÷ alvo. Os padrões são os da ActiveView.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    {FIELDS.map((field) => (
                        <label key={field.key} className="grid grid-cols-[1fr_7rem] items-start gap-3">
                            <span>
                                <span className="text-sm font-medium text-gray-800">{field.label}</span>
                                <span className="block text-xs text-gray-500">{field.help}</span>
                            </span>
                            <input
                                type="number"
                                step={field.step}
                                min={field.min}
                                value={draft[field.key]}
                                onChange={(e) =>
                                    setDraft((d) => ({ ...d, [field.key]: Number(e.target.value) }))
                                }
                                className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </label>
                    ))}
                    {invalid && (
                        <p className="text-xs text-red-600">O piso não pode ser maior que o teto.</p>
                    )}
                </div>

                <div className="flex items-center justify-between pt-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                            setDraft((d) => ({
                                ...d,
                                ...Object.fromEntries(
                                    FIELDS.map((f) => [f.key, DEFAULT_LINEAR_PARAMS[f.key]]),
                                ),
                            }))
                        }
                    >
                        Voltar aos padrões
                    </Button>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)}>
                            Cancelar
                        </Button>
                        <Button
                            disabled={invalid}
                            onClick={() => {
                                onApply(
                                    Object.fromEntries(FIELDS.map((f) => [f.key, draft[f.key]])),
                                );
                                onOpenChange(false);
                            }}
                        >
                            Aplicar
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
